import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, loginWithGoogle } = useAuth();
  const mid = params.get('mid');
  const [mosque, setMosque] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(
    (params.get('err') === 'superadmin' || params.get('err') === 'google')
      ? 'Login dengan Google gagal. Silakan coba lagi.' : ''
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mid) {
      axios.get(`${API_URL}/api/mosques/${mid}`).then((r) => setMosque(r.data)).catch(() => {});
    }
  }, [mid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      const returnPath = localStorage.getItem('auth_return');
      localStorage.removeItem('auth_return');
      const role = result.user?.role;
      navigate(
        returnPath && returnPath.startsWith('/region/') ? returnPath :
        role === 'super_admin' ? '/superadmin' :
        role === 'takmir' ? '/admin' :
        role === 'merchant' ? '/merchant' :
        role === 'upz' ? '/upz' :
        (role === 'kemenag_kab' || role === 'kemenag_prov') ? '/kemenag' : '/jamaah'
      );
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // Pass existing auth_return through Google flow
    loginWithGoogle(localStorage.getItem('auth_return') || null);
  };

  const title = mosque
    ? (/^masjid/i.test(mosque.name) ? `Login Admin ${mosque.name}` : `Login Admin Masjid ${mosque.name}`)
    : 'Masuk ke Akun Anda';
  const subtitle = mosque ? `${mosque.village_name || ''}, ${mosque.regency_name || ''}` : 'masjidberdaya.id';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-emerald-50 to-emerald-100 flex items-center justify-center px-6 py-8"
    >
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl border border-stone-100 p-8"
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4 overflow-hidden" data-testid="login-mosque-logo">
              {mosque?.logo_url ? (
                <img src={mosque.logo_url} alt={mosque.name} className="w-full h-full object-cover" />
              ) : mosque ? (
                <Building2 className="w-10 h-10 text-emerald-700" />
              ) : (
                <Lock className="w-10 h-10 text-emerald-700" />
              )}
            </div>
            <h1 className="text-2xl font-heading font-bold text-primary-dark mb-2" data-testid="login-title">
              {title}
            </h1>
            <p className="text-sm font-body text-text-secondary">{subtitle}</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6"
              data-testid="login-error"
            >
              <p className="text-sm font-body text-red-800">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-body font-medium text-text-primary mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all font-body text-sm"
                  placeholder="admin@masjid.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-body font-medium text-text-primary mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-2xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all font-body text-sm"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 text-white font-body font-semibold py-3 rounded-full transition-colors"
            >
              {loading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs font-body text-stone-400">atau</span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 border border-stone-200 bg-white hover:bg-stone-50 text-stone-800 font-body font-semibold py-3 rounded-full transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Masuk dengan Google
          </button>

          <div className="mt-6 text-center space-y-2">
            <button
              onClick={() => navigate('/daftar-masjid')}
              data-testid="login-register-link"
              className="block w-full text-sm font-body text-emerald-700 hover:text-emerald-800 font-medium"
            >
              Belum terdaftar? Daftarkan Masjid Anda
            </button>
            <button
              onClick={() => navigate('/daftar-umkm')}
              data-testid="login-merchant-register-link"
              className="block w-full text-sm font-body text-amber-700 hover:text-amber-800 font-medium"
            >
              Punya usaha? Daftar sebagai Merchant UMKM
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm font-body text-stone-500 hover:text-stone-700 font-medium"
            >
              ← Kembali ke Beranda
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoginPage;
